import DN from "owp.dn";
import type { StringRecord } from "@gss/types/StringRecord";
import { sweCompare } from "./sweCompare";

export interface TreeNode<T> {
    dn: string;
    name: string;
    nameLong: string;
    data?: T;
    children: TreeNode<T>[];
    childrenMap: StringRecord<TreeNode<T>>;
}

export class OrgTree<O, T> {
    private readonly depth: number;
    public readonly root: TreeNode<T>;

    public static getLeafNodes<T>(node: TreeNode<T>): TreeNode<T>[] {
        const result: TreeNode<T>[] = [];
        const addLeafNodes = (node: TreeNode<T>) => {
            // Parent node
            if (node.children.length > 0) {
                for (const child of node.children) {
                    addLeafNodes(child);
                }
            }
            // Leaf node
            else {
                result.push(node);
            }
        };
        addLeafNodes(node);
        return result;
    }

    public constructor(
        organisations: O[],
        getDnCallback: (org: O) => string,
        getDataCallback?: (org: O) => T,
    ) {
        let depth = 0;
        const tree: TreeNode<T> = {
            name: "",
            nameLong: "",
            dn: "",
            children: [],
            childrenMap: {},
        };

        for (const org of organisations) {
            const dn = new DN(getDnCallback(org));
            depth = Math.max(depth, dn.length());
            let node = tree;
            for (let i = 0; i < dn.length(); ++i) {
                const rdn = dn.getRDN(i);
                if (rdn == null) {
                    throw new Error(
                        `Missing RDN for organisation: ${dn.value}`,
                    );
                }
                // toString includes both attribute and value
                const shortPath = rdn.toString();
                if (node.childrenMap[shortPath] == null) {
                    const nodeDn = dn.subDn(i + 1);
                    const newChild: TreeNode<T> = {
                        name: rdn.value,
                        nameLong: getLongName(nodeDn),
                        dn: nodeDn.value,
                        children: [],
                        childrenMap: {},
                    };
                    node.childrenMap[shortPath] = newChild;
                    node.children.push(newChild);
                }
                node = node.childrenMap[shortPath];
            }
            if (getDataCallback != null) {
                node.data = getDataCallback(org);
            }
        }

        if (tree.children.length !== 1) {
            throw new Error("Expected exactly one root node.");
        }

        // Get root node.
        this.root = tree.children[0];
        this.depth = depth;
        this.sort(this.root);
    }

    private sort(node: TreeNode<T>): void {
        node.children = node.children.toSorted((a, b) =>
            sweCompare(a.name, b.name),
        );
        for (const child of node.children) {
            this.sort(child);
        }
    }

    public findOrganisation(dn: string): TreeNode<T> | undefined {
        return findOrganisationInner(this.root, dn);
    }
}

function findOrganisationInner<T>(
    node: TreeNode<T>,
    dn: string,
): TreeNode<T> | undefined {
    if (node.dn === dn) {
        return node;
    }
    for (const n of node.children) {
        const res = findOrganisationInner(n, dn);
        if (res != null) {
            return res;
        }
    }
    return undefined;
}

export function getLongName(dn: DN): string {
    return dn.rdns
        .map((rdn) => rdn.value)
        .toReversed()
        .join(" | ");
}
